import React from 'react';
import { GridCellProps } from 'react-virtualized';
import { ITableSettings } from '../../interfaces';
import { useChartDataTable } from '../useChartDataTable';
import * as ChartDataTableUI from '../ChartDataTableUI';
import { assertCondition } from '../../../../utils/types';
import { getCharacterByIncrementedIndex } from '../../../../utils/character';
import {
  useTableColumnSortState,
  useTableColumnWidthState,
} from '../../../MultiGridTable';
import HeaderCell from './HeaderCell';
import { calculateMeasureIndex } from '../utils';
import { useChartContext } from '../../ChartContext';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';

interface IProps {
  analysisId: string;
  gridCellProps: GridCellProps;
  dataTableData: ReturnType<typeof useChartDataTable>;
  columnWidthsData: ReturnType<typeof useTableColumnWidthState>;
  columnSortStateData: ReturnType<typeof useTableColumnSortState>;
  tableSettings: ITableSettings;
}

const HeaderCellRenderer = ({
  analysisId,
  gridCellProps,
  dataTableData,
  columnWidthsData,
  columnSortStateData,
  tableSettings,
}: IProps) => {
  const { chartData } = useChartContext();

  const { columnIndex, rowIndex, key, style } = gridCellProps;
  const { headers, breakdowns, segments, hasComparison } = dataTableData;
  const { columnWidths, handleMouseDown, isResizing } = columnWidthsData;
  const { sortColumnIndex, sortDirection, onSort } = columnSortStateData;

  // calculate the absolute column index after breakdowns
  const colIndex = columnIndex - breakdowns.length;
  const numComparisonColumns = tableSettings.showPercentDelta ? 3 : 2;

  // if the column is the first column and there are breakdowns, render empty header cells for breakdowns
  if (columnIndex < breakdowns.length && columnIndex === 0) {
    const width = columnWidths
      .slice(0, breakdowns.length)
      .reduce((a: number, b: number) => a + b, 0);

    return (
      <ChartDataTableUI.HeaderCell
        key={key}
        style={{
          ...style,
          width,
        }}
        $columnIndex={columnIndex}
        $rowIndex={rowIndex}
        $isEmptyCell
        $isBackgroundFilled
      />
    );
  }

  if (colIndex >= 0) {
    // compute the measureIndex based on the number of segments and breakdowns.
    const measureIndex = calculateMeasureIndex(
      colIndex,
      segments,
      hasComparison,
      numComparisonColumns,
    );

    // measureIndex must be less than the number of measureControls
    // otherwise we have a bug
    assertCondition(
      chartData.queryType === QueryType.DataTable &&
        measureIndex < chartData.measureControls.length,
    );

    // if no segments, render the header as normal
    if (segments.length === 0 && !hasComparison) {
      const prefix = getCharacterByIncrementedIndex('A', colIndex);
      const isColumnSorted = sortColumnIndex === columnIndex;
      const headerName = `${prefix}: ${headers[colIndex]}`;

      return (
        <HeaderCell
          analysisId={analysisId}
          measureIndex={measureIndex}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          style={style}
          dataTableData={dataTableData}
          headerName={headerName}
          onSort={onSort}
          handleMouseDown={handleMouseDown}
          isColumnSorted={isColumnSorted}
          sortDirection={sortDirection}
          isResizing={isResizing}
        />
      );
    }

    const numMeasureColumns =
      (segments.length ? segments.length : 1) *
      (hasComparison ? numComparisonColumns : 1);

    // if there are segments, render the header as a spanning header across
    // all the segment columns
    if (colIndex % numMeasureColumns === 0) {
      // Calculate the index for the spanning headers
      const headerMeasureIndex = Math.floor(colIndex / numMeasureColumns);
      const prefix = getCharacterByIncrementedIndex('A', headerMeasureIndex);
      const measureName = `${prefix}: ${headers[headerMeasureIndex]}`;

      const width = columnWidths
        .slice(columnIndex, columnIndex + numMeasureColumns)
        .reduce((a: number, b: number) => a + b, 0);

      return (
        <HeaderCell
          analysisId={analysisId}
          measureIndex={measureIndex}
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          style={{
            ...style,
            width,
          }}
          dataTableData={dataTableData}
          headerName={measureName}
          onSort={onSort}
          handleMouseDown={handleMouseDown}
          sortDirection={sortDirection}
          isResizing={isResizing}
        />
      );
    }
  }

  return null;
};

export default HeaderCellRenderer;
